import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import ThemeConstants from "../../../../config/theme/theme_constants";
import { useHandleSurvey } from "../../domain/hooks/handle_survey";
import { useCallback, useEffect, useState, useRef } from "react";
import { SyncLoader } from "react-spinners";
import AnswersDialog from "../components/answers_dialog";
import SurveyCommentDialog from "../components/survey_comment_dialog";
import UvibesPointIcon from '../../../../assets/images/uvibes_point.svg?react';
import EditNoteIcon from '../../../../assets/images/edit_note_icon.svg?react';
import { useTranslation } from '../../../../i18n/useTranslation';

const SurveyAnsweredPage: React.FC = () => {
    const { t } = useTranslation();
    const theme = ThemeConstants();
    const navigate = useNavigate();
    const { surveyId } = useParams();
    const location = useLocation();
    const { survey: initialSurvey } = location.state || {};

    const {
        survey,
        isLoading,
        error,
        refetch
    } = useHandleSurvey(surveyId, initialSurvey);

    const [isAnswersDialogOpen, setIsAnswersDialogOpen] = useState(false);
    const [isSurveyCommentDialogOpen, setIsSurveyCommentDialogOpen] = useState(false);
    const viewResultsButtonRef = useRef<HTMLButtonElement>(null);
    const surveyCommentButtonRef = useRef<HTMLButtonElement>(null);

    const handleOpenAnswersDialog = useCallback(() => {
        if (viewResultsButtonRef.current) {
            viewResultsButtonRef.current.blur();
        }
        setIsAnswersDialogOpen(true);
    }, []);

    const handleOpenSurveyCommentDialog = useCallback(() => {
        if (surveyCommentButtonRef.current) {
            surveyCommentButtonRef.current.blur();
        }
        setIsSurveyCommentDialogOpen(true);
    }, []);

    useEffect(() => {
        if (survey === null) {
            toast.error(t('survey.not_found'));
            navigate('/survey', { replace: true } );
        } else if (survey && survey.answered === false) {
            const firstNotAnswered = survey.questions.findIndex((question) => question.initialAnswer === undefined);
            if (firstNotAnswered !== -1) {
                navigate(`/survey/${survey.id}/${survey.questions[firstNotAnswered].id}`, { replace: true } );
            }
        } else if (survey && error?.status === 400) {
            toast.error(t('survey.not_found'));
            navigate('/survey', { replace: true } );
        }
    }, [survey, error, navigate, initialSurvey, t]);

    return (
        <div className={`min-h-dvh flex flex-col items-center md:pt-[7.4dvh] max-md:pt-[3.7dvh] pb-[3.7dvh] px-3 w-full gap-8 ${survey ? 'max-md:justify-between md:justify-center md:gap-12' : 'justify-center'}`}>
            {isLoading 
                ? <SyncLoader color={theme.UvibesPink} size={24} speedMultiplier={0.5}/>
                : error !== null
                    ? <div className="flex flex-col items-center gap-5">
                        <p className="text-white font-semibold text-center text-sm">{t('survey.fetch_error')}</p>
                        <button
                            className="rounded-full text-white font-bold px-8 py-2.5 min-h-10.5 min-w-51 cursor-pointer"
                            style={{ backgroundColor: theme.UvibesPink }}
                            onClick={() => refetch()}
                        >
                            {t('common.button.retry')}
                        </button>
                    </div>
                    : <>
                        <div className="flex flex-col items-center gap-3.75">
                            <h1 className="font-bold text-[25px]" style={{ color: theme.UvibesPink }}>{t('survey.congrats')}</h1>
                            <div className="flex flex-col items-center gap-2.5">
                                <img className="max-w-60 w-[60%] aspect-square object-contain" src="/images/dancing_people.png" alt={t('survey.dancing_alt')} height={240} width={240}/>
                                <p className="text-center leading-relaxed text-xs max-w-95">{t('survey.finished', { points: survey?.nbQuestions })}</p>
                            </div>
                        </div>

                        <div className="flex flex-col items-center gap-2.5 w-full max-w-95">
                            <div
                                className="flex items-center justify-center gap-7 w-full p-4 rounded-[30px] aspect-21/11"
                                style={{
                                    backgroundColor: theme.UvibesPink,
                                    boxShadow: '0 0 4px rgba(0, 0, 0, 0.04), 0 8px 16px rgba(0, 0, 0, 0.08)'
                                }}
                            >
                                <span className="font-bold text-white text-center text-[80px]">{`+${survey?.nbQuestions}`}</span>
                                <UvibesPointIcon className="h-20 w-20"/>
                            </div>
                            <button
                                ref={surveyCommentButtonRef}
                                className="w-full bg-white px-8 py-2.25 rounded-[15px] cursor-pointer flex items-center gap-2.5 justify-center"
                                style={{
                                    color: theme.UvibesPink,
                                    boxShadow: `inset 0 0 0 1px ${theme.UvibesPink}`
                                }}
                                onClick={handleOpenSurveyCommentDialog}
                            >
                                <EditNoteIcon className="h-6 w-6"/>
                                <span className="text-center">{t('survey.feedback')}</span>
                            </button>
                        </div>

                        <div className="flex flex-col items-center gap-2.5 w-full max-w-65">
                            <button
                                className="font-bold w-full text-white text-center px-8 py-2.25 rounded-[15px] cursor-pointer"
                                style={{ backgroundColor: theme.UvibesPink }}
                                onClick={() => navigate('/survey')}
                            >
                                {t('survey.new')}
                            </button>

                            {survey?.isResultPublic === true && <button
                                ref={viewResultsButtonRef}
                                className="font-bold w-full bg-white text-center px-8 py-2.25 rounded-[15px] cursor-pointer"
                                style={{
                                    color: theme.UvibesPink,
                                    boxShadow: `inset 0 0 0 1px ${theme.UvibesPink}`
                                }}
                                onClick={handleOpenAnswersDialog}
                            >
                                {t('survey.results')}
                            </button>}

                            <button
                                className="font-bold w-full bg-white text-center px-8 py-2.25 rounded-[15px] cursor-pointer"
                                style={{
                                    color: theme.UvibesPink,
                                    boxShadow: `inset 0 0 0 1px ${theme.UvibesPink}`
                                }}
                                onClick={() => navigate('/')}
                            >
                                {t('common.button.finish')}
                            </button>
                        </div>
                        <AnswersDialog
                            open={isAnswersDialogOpen}
                            onClose={() => setIsAnswersDialogOpen(false)}
                            surveyId={survey?.id}
                        />
                        <SurveyCommentDialog
                            open={isSurveyCommentDialogOpen}
                            onClose={() => setIsSurveyCommentDialogOpen(false)}
                            onValidate={() => setIsSurveyCommentDialogOpen(false)}
                            surveyId={survey?.id || ""}
                            initialComment={survey?.surveyComment}
                        />
                    </>}
        </div>
    );
}

export default SurveyAnsweredPage;